import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiConfigService } from './api-config.service';
import {
  AgentPresenceDto,
  ApiErrorResponse,
  ApiHealthResponse,
  BurndownResponse,
  ChapterDto,
  CurrentUserResponse,
  DirectPostPrepareRequest,
  DirectPostPrepareResponse,
  DirectPostResultPayload,
  InvoiceDto,
  MembershipDto,
  PaymentResultVerificationResponse,
  PaymentStatusResponse,
  PledgeDto,
  ProjectRole,
  ProjectSummary,
  RoadmapProjectionResponse,
  SprintDto,
  SubscriptionDto,
  SupportPortalInfo,
  TaskDto,
  TaskMoveRequest,
  TeamDto,
  TeamBudgetResponse,
  TeamBudgetTopupRequest,
  UserProfile
} from '../models/api-dto.model';
import {
  NabDirectPostPayload
} from '../models/subscription.model';

@Injectable({
  providedIn: 'root'
})
export class BackendApiService {
  /**
   * Production: https://api.sprinta.io/v1
   * Development: https://dev-api.sprinta.io/v1
   * Endpoint paths begin after /v1 per Section 3 of the contract recommendation.
   * e.g. GET https://api.sprinta.io/v1/projects/{projectId}/tasks
   */
  readonly baseUrl = environment.apiBaseUrl || 'https://api.sprinta.io/v1';
  readonly agentBaseUrl = (environment as any).agentApiBaseUrl || 'https://agent-api.sprinta.io/v1';

  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfigService
  ) {}

  get isBackendApiEnabled(): boolean {
    return this.apiConfig.isLiveMode;
  }

  // -------------------------------------------------------------------------
  // 0. Deployment Health Check (Unauthenticated)
  // -------------------------------------------------------------------------
  getHealth(): Observable<ApiHealthResponse> {
    return this.http.get<ApiHealthResponse>(`${this.baseUrl}/health`);
  }

  // -------------------------------------------------------------------------
  // 1. Identity & Projects (Section 9.1)
  // -------------------------------------------------------------------------
  getCurrentUser(): Observable<CurrentUserResponse> {
    return this.http.get<CurrentUserResponse>(`${this.baseUrl}/me`);
  }

  updateProfile(profile: { displayName?: string; avatarUrl?: string; email?: string; preferences?: Record<string, any> }): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${this.baseUrl}/me`, profile);
  }

  getProjects(): Observable<ProjectSummary[]> {
    return this.http.get<ProjectSummary[]>(`${this.baseUrl}/projects`);
  }

  createProject(payload: { name: string; description?: string }): Observable<ProjectSummary> {
    return this.http.post<ProjectSummary>(`${this.baseUrl}/projects`, payload);
  }

  getProject(projectId: string): Observable<ProjectSummary> {
    return this.http.get<ProjectSummary>(`${this.baseUrl}/projects/${projectId}`);
  }

  updateProject(projectId: string, updates: Partial<ProjectSummary>): Observable<ProjectSummary> {
    return this.http.patch<ProjectSummary>(`${this.baseUrl}/projects/${projectId}`, updates);
  }

  archiveProject(projectId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/projects/${projectId}/archive`, {});
  }

  deleteProject(projectId: string): Observable<{ deleted: boolean; projectId: string }> {
    return this.http.delete<{ deleted: boolean; projectId: string }>(`${this.baseUrl}/projects/${projectId}`);
  }

  // -------------------------------------------------------------------------
  // 2. Tasks & Backlog (Section 9.2)
  // -------------------------------------------------------------------------
  getTasks(
    projectId: string,
    filters?: { sprintId?: string; chapterId?: string; status?: string; limit?: number; cursor?: string; visibility?: string; supportStage?: string }
  ): Observable<{ tasks: TaskDto[]; nextCursor?: string }> {
    let params = new HttpParams();
    if (filters?.sprintId) params = params.set('sprintId', filters.sprintId);
    if (filters?.chapterId) params = params.set('chapterId', filters.chapterId);
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.limit) params = params.set('limit', filters.limit.toString());
    if (filters?.cursor) params = params.set('cursor', filters.cursor);
    if (filters?.visibility) params = params.set('visibility', filters.visibility);
    if (filters?.supportStage) params = params.set('supportStage', filters.supportStage);

    return this.http.get<{ tasks: TaskDto[]; nextCursor?: string }>(`${this.baseUrl}/projects/${projectId}/tasks`, { params });
  }

  createTask(projectId: string, task: Partial<TaskDto>): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks`, task);
  }

  batchCreateTasks(projectId: string, tasks: Partial<TaskDto>[]): Observable<{ tasks: TaskDto[] }> {
    return this.http.post<{ tasks: TaskDto[] }>(`${this.baseUrl}/projects/${projectId}/tasks/batch`, { tasks });
  }

  getTask(projectId: string, taskId: string): Observable<TaskDto> {
    return this.http.get<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}`);
  }

  updateTask(projectId: string, taskId: string, updates: Partial<TaskDto>, expectedVersion: number): Observable<TaskDto> {
    return this.http.patch<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}`, {
      ...updates,
      expectedVersion
    });
  }

  deleteTask(projectId: string, taskId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}`);
  }

  moveTask(projectId: string, taskId: string, move: TaskMoveRequest): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/move`, move);
  }

  addTaskNote(projectId: string, taskId: string, text: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/notes`, { text });
  }

  getTaskNotes(projectId: string, taskId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/notes`);
  }

  deleteTaskNote(projectId: string, taskId: string, noteId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/notes/${noteId}`);
  }

  // -------------------------------------------------------------------------
  // 3. Sprints (Section 9.3)
  // -------------------------------------------------------------------------
  getSprints(projectId: string): Observable<SprintDto[]> {
    return this.http.get<SprintDto[]>(`${this.baseUrl}/projects/${projectId}/sprints`);
  }

  createSprint(projectId: string, teamId: string, sprint: { title: string; startDate: string; endDate: string }): Observable<SprintDto> {
    return this.http.post<SprintDto>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}/sprints`, sprint);
  }

  getSprint(projectId: string, sprintId: string): Observable<SprintDto> {
    return this.http.get<SprintDto>(`${this.baseUrl}/projects/${projectId}/sprints/${sprintId}`);
  }

  addTasksToSprint(projectId: string, sprintId: string, taskIds: string[]): Observable<TaskDto[]> {
    return this.http.post<TaskDto[]>(`${this.baseUrl}/projects/${projectId}/sprints/${sprintId}/tasks`, { taskIds });
  }

  completeSprint(projectId: string, sprintId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/sprints/${sprintId}/complete`, {});
  }

  getSprintBurndown(projectId: string, sprintId: string): Observable<BurndownResponse> {
    return this.http.get<BurndownResponse>(`${this.baseUrl}/projects/${projectId}/sprints/${sprintId}/burndown`);
  }

  // -------------------------------------------------------------------------
  // 4. Chapters & Roadmap (Section 9.4)
  // -------------------------------------------------------------------------
  getChapters(projectId: string): Observable<ChapterDto[]> {
    return this.http.get<ChapterDto[]>(`${this.baseUrl}/projects/${projectId}/chapters`);
  }

  createChapter(projectId: string, chapter: { title: string; description: string }): Observable<ChapterDto> {
    return this.http.post<ChapterDto>(`${this.baseUrl}/projects/${projectId}/chapters`, chapter);
  }

  updateChapter(projectId: string, chapterId: string, updates: Partial<ChapterDto>): Observable<ChapterDto> {
    return this.http.patch<ChapterDto>(`${this.baseUrl}/projects/${projectId}/chapters/${chapterId}`, updates);
  }

  deleteChapter(projectId: string, chapterId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/chapters/${chapterId}`);
  }

  getRoadmap(projectId: string): Observable<RoadmapProjectionResponse> {
    return this.http.get<RoadmapProjectionResponse>(`${this.baseUrl}/projects/${projectId}/roadmap`);
  }

  // -------------------------------------------------------------------------
  // 5. Teams, Members & Invitations (Section 9.5)
  // -------------------------------------------------------------------------
  getTeams(projectId: string): Observable<TeamDto[]> {
    return this.http.get<TeamDto[]>(`${this.baseUrl}/projects/${projectId}/teams`);
  }

  createTeam(projectId: string, team: { title: string; description: string; defaultVelocity?: number }): Observable<TeamDto> {
    return this.http.post<TeamDto>(`${this.baseUrl}/projects/${projectId}/teams`, team);
  }

  getTeam(projectId: string, teamId: string): Observable<TeamDto> {
    return this.http.get<TeamDto>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}`);
  }

  updateTeam(projectId: string, teamId: string, updates: Partial<TeamDto>): Observable<TeamDto> {
    return this.http.patch<TeamDto>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}`, updates);
  }

  deleteTeam(projectId: string, teamId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}`);
  }

  getTeamAgentBudget(projectId: string, teamId: string): Observable<TeamBudgetResponse> {
    return this.http.get<TeamBudgetResponse>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}/agent-budget`);
  }

  topupTeamAgentBudget(projectId: string, teamId: string, payload: TeamBudgetTopupRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/teams/${teamId}/agent-budget/topup`, payload);
  }

  createInvitation(projectId: string, invite: { email: string; projectRole: ProjectRole; teamId?: string; allocatedChapterIds?: string[] }): Observable<MembershipDto> {
    return this.http.post<MembershipDto>(`${this.baseUrl}/projects/${projectId}/invitations`, invite);
  }

  previewInvitation(token: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/invitations/${token}`);
  }

  acceptInvitation(token: string): Observable<MembershipDto> {
    return this.http.post<MembershipDto>(`${this.baseUrl}/invitations/${token}/accept`, {});
  }

  resendInvitation(projectId: string, invitationId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/projects/${projectId}/invitations/${invitationId}/resend`, {});
  }

  cancelInvitation(projectId: string, invitationId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/invitations/${invitationId}`);
  }

  updateMember(projectId: string, memberId: string, updates: Partial<MembershipDto>): Observable<MembershipDto> {
    return this.http.patch<MembershipDto>(`${this.baseUrl}/projects/${projectId}/members/${memberId}`, updates);
  }

  removeMember(projectId: string, memberId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/members/${memberId}`);
  }

  // -------------------------------------------------------------------------
  // 6. Subscriptions & Invoices (Section 9.6)
  // -------------------------------------------------------------------------
  getSubscription(projectId: string): Observable<SubscriptionDto> {
    return this.http.get<SubscriptionDto>(`${this.baseUrl}/projects/${projectId}/subscription`);
  }

  purchaseSubscription(projectId: string, payload: { unitsPurchased: number }): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.baseUrl}/projects/${projectId}/subscription/purchase`, payload);
  }

  renewSubscription(projectId: string): Observable<SubscriptionDto> {
    return this.http.post<SubscriptionDto>(`${this.baseUrl}/projects/${projectId}/subscription/renew`, {});
  }

  adjustSubscription(projectId: string, payload: { unitsPurchased: number; autoRenew?: boolean }): Observable<SubscriptionDto> {
    return this.http.patch<SubscriptionDto>(`${this.baseUrl}/projects/${projectId}/subscription`, payload);
  }

  getInvoices(projectId: string): Observable<InvoiceDto[]> {
    return this.http.get<InvoiceDto[]>(`${this.baseUrl}/projects/${projectId}/invoices`);
  }

  getInvoice(projectId: string, invoiceId: string): Observable<InvoiceDto> {
    return this.http.get<InvoiceDto>(`${this.baseUrl}/projects/${projectId}/invoices/${invoiceId}`);
  }

  getInvoiceDownloadUrl(projectId: string, invoiceId: string): Observable<{ downloadUrl: string }> {
    return this.http.get<{ downloadUrl: string }>(`${this.baseUrl}/projects/${projectId}/invoices/${invoiceId}/download`);
  }

  // -------------------------------------------------------------------------
  // 7. Payment Processing: NAB Transact Direct Post v2 (Corrected Endpoints)
  // -------------------------------------------------------------------------

  /**
   * Step 1: Request Direct Post preparation immediately before payment.
   * Path: POST /projects/{projectId}/payments/direct-post/prepare
   * The backend creates and binds a pending payment attempt to project, amount, licence quantity, currency, and reference.
   */
  prepareDirectPost(projectId: string, payload: DirectPostPrepareRequest): Observable<DirectPostPrepareResponse> {
    return this.http.post<DirectPostPrepareResponse>(`${this.baseUrl}/projects/${projectId}/payments/direct-post/prepare`, payload);
  }

  /**
   * Step 2: Client posts raw card details directly to NAB Transact.
   * Direct Post keeps raw card data out of Sprinta's API and storage.
   * Endpoint and parameters are provisional pending official documentation verification.
   */
  submitToNabTransactDirectPost(nabDirectPostUrl: string, payload: NabDirectPostPayload): Observable<string> {
    const body = new URLSearchParams();
    Object.keys(payload).forEach(key => {
      if (payload[key] !== undefined && payload[key] !== null) {
        body.set(key, payload[key]);
      }
    });
    return this.http.post(nabDirectPostUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      responseType: 'text'
    });
  }

  /**
   * Step 3: Server-side payment verification.
   * Path: POST /projects/{projectId}/payments/direct-post/result
   * Sprinta does NOT activate subscriptions on unverified browser responses.
   * The backend verifies transaction validity and updates DynamoDB.
   */
  submitPaymentResult(projectId: string, resultPayload: DirectPostResultPayload): Observable<PaymentResultVerificationResponse> {
    return this.http.post<PaymentResultVerificationResponse>(`${this.baseUrl}/projects/${projectId}/payments/direct-post/result`, resultPayload);
  }

  /**
   * Poll payment status by reference.
   * Path: GET /projects/{projectId}/payments/direct-post/status/{referenceId}
   */
  getPaymentStatus(projectId: string, referenceId: string): Observable<PaymentStatusResponse> {
    return this.http.get<PaymentStatusResponse>(`${this.baseUrl}/projects/${projectId}/payments/direct-post/status/${referenceId}`);
  }

  // -------------------------------------------------------------------------
  // 8. Project Export & Import (Section 9.7)
  // -------------------------------------------------------------------------
  createExport(projectId: string): Observable<{ exportId: string; status: string }> {
    return this.http.post<{ exportId: string; status: string }>(`${this.baseUrl}/projects/${projectId}/exports`, {});
  }

  getExport(projectId: string, exportId: string): Observable<{ exportId: string; status: string }> {
    return this.http.get<{ exportId: string; status: string }>(`${this.baseUrl}/projects/${projectId}/exports/${exportId}`);
  }

  getExportDownloadUrl(projectId: string, exportId: string): Observable<{ downloadUrl: string }> {
    return this.http.get<{ downloadUrl: string }>(`${this.baseUrl}/projects/${projectId}/exports/${exportId}/download`);
  }

  // -------------------------------------------------------------------------
  // 9. Jira Cloud Integration
  // -------------------------------------------------------------------------
  testJiraConnection(projectId: string, credentials: { jiraDomain: string; email: string; apiToken: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/integrations/jira/test-connection`, credentials);
  }

  getJiraProjects(projectId: string, credentials: { jiraDomain: string; email: string; apiToken: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/integrations/jira/projects`, credentials);
  }

  previewJiraImport(projectId: string, payload: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/integrations/jira/preview`, payload);
  }

  // -------------------------------------------------------------------------
  // 10. User Support Portal & Community Workflow
  // -------------------------------------------------------------------------
  getSupportPortalInfo(projectId: string): Observable<SupportPortalInfo> {
    return this.http.get<SupportPortalInfo>(`${this.baseUrl}/projects/${projectId}/support/info`);
  }

  joinSupportPortal(projectId: string): Observable<MembershipDto> {
    return this.http.post<MembershipDto>(`${this.baseUrl}/projects/${projectId}/support/join`, {});
  }

  moderateTask(projectId: string, taskId: string): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/moderate`, {});
  }

  estimateTask(projectId: string, taskId: string, estimatedAmount: number): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/estimate`, { estimatedAmount });
  }

  sponsorTask(projectId: string, taskId: string): Observable<TaskDto> {
    return this.http.post<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/sponsor`, {});
  }

  createPledge(
    projectId: string,
    taskId: string,
    pledge: { amount: number; transactionId: string; referenceId?: string; cardLast4?: string; cardBrand?: string }
  ): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/pledges`, pledge);
  }

  getPledges(projectId: string, taskId: string): Observable<PledgeDto[]> {
    return this.http.get<PledgeDto[]>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}/pledges`);
  }

  archiveTask(projectId: string, taskId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}`);
  }

  patchTask(projectId: string, taskId: string, patch: any): Observable<TaskDto> {
    return this.http.patch<TaskDto>(`${this.baseUrl}/projects/${projectId}/tasks/${taskId}`, patch);
  }

  // -------------------------------------------------------------------------
  // 11. Sprinta Agent Presence & Session Monitoring
  // -------------------------------------------------------------------------
  getAgentPresence(developerIds: string[]): Observable<{ presence: AgentPresenceDto[] }> {
    if (!developerIds || developerIds.length === 0) {
      return of({ presence: [] });
    }
    const params = new HttpParams().set('developerIds', developerIds.join(','));
    return this.http.get<{ presence: AgentPresenceDto[] }>(`${this.agentBaseUrl}/agent/presence`, { params }).pipe(
      catchError(() => of({ presence: [] }))
    );
  }
}

